import {Buffer} from 'buffer';
import {MoodLightCategory} from 'puffco-api-axios-client';

import {Constants} from '../../../../constants';
import {
  fromSecondsTimestamp,
  getMiddlewareValueByteSize,
  getSecondsFromMilliseconds,
} from '../../../utils';
import {convertBufferValue} from '../../../utils/convertReadValue';
import {writeValueToBuffer} from '../../../utils/convertWriteValue';
import {
  ScratchpadLanternCustom,
  ScratchpadProfileCustom,
  ScratchpadType,
  ScratchpadVersion,
} from './types';
import {encodings, versionField} from './versions';

type AggregatedScratchpad = {
  version: ScratchpadVersion;
  moodUlid?: string;
  moodName?: string;
  moodDateModified?: number;
  moodType?: number;
  tempo?: number;
  colors?: string[];
  originalMoodUlid?: string;
  heatProfileUlid?: string;
  heatProfileDateModified?: number;
};

type BasicExclusiveMoodLight = {id: string; category: MoodLightCategory};

type BasicCustomMoodLight = {
  id: string;
  category: MoodLightCategory;
  name: string;
  modified: string;
  projector: {
    meta: {
      userColors?: string[];
      tempoFrac?: number;
      moodType?: number;
      originalMoodLightId?: string;
    };
  };
};

export type BasicMoodLight = BasicCustomMoodLight & BasicExclusiveMoodLight;
export type BasicExclusiveOrCustomMoodLight =
  | BasicCustomMoodLight
  | BasicExclusiveMoodLight;

export interface FromMoodLightOrProfile {
  moodLight?: BasicMoodLight;
  profile?: {id: string; modified: string};
}
export type ToMoodLightOrHeatProfile = {
  moodLight?: BasicExclusiveOrCustomMoodLight;
  profile?: {id: string; modified: string};
};

type ConstructorOptions = FromMoodLightOrProfile | ScratchpadType;

const isScratchpad = (options: ConstructorOptions): options is ScratchpadType =>
  Object.hasOwn(options, 'version');

const trimChar = (value: string, code = 0) => {
  const char = String.fromCharCode(code);

  return value.replace(new RegExp(`${char}+$`), '');
};

const hasLength = <T>(item: T): item is T & {length: number} =>
  (item as any).length;
/**
 * This class provides methods of manipulating data that is stored in addition to heat cycles required
 * settings (temperature, duration, etc). It is known as Scratchpad and it is a LED API 2 concept.
 *
 * Data stored by the scratchpad contains mood light and heat profile information used to bind the heat
 * cycle back to its original entity when reading the settings from the device.
 */
export class Scratchpad {
  private readonly scratchpad: ScratchpadType;

  constructor(options: ConstructorOptions) {
    if (isScratchpad(options)) {
      this.scratchpad = options;

      return;
    }

    const data = Scratchpad.aggregate(options);

    this.scratchpad = Scratchpad.sanitize(data);
  }

  get value() {
    return this.scratchpad;
  }

  public serialize(): number[] {
    const {specs, version} = Scratchpad.getSpecificationIfExists(
      this.scratchpad.version,
    );

    const buffer = Buffer.alloc(
      Constants.SCRATCHPAD_MAX_SIZE,
      version == ScratchpadVersion.BLANK ? 0xff : 0,
    );

    const versionBuffer = writeValueToBuffer(version, versionField.type);

    buffer.set(versionBuffer, 0);

    let index = getMiddlewareValueByteSize(versionField.type);

    specs.forEach(field => {
      const newbuff = writeValueToBuffer(
        this.scratchpad[field.key as keyof ScratchpadType],
        field.type,
      );
      buffer.set(newbuff, index);
      index += hasLength(field)
        ? field.length
        : getMiddlewareValueByteSize(field.type);
    });

    return [...buffer];
  }

  public static parse(data: number[]): Scratchpad {
    const buffer = Buffer.from(data);

    const version = !buffer.length
      ? ScratchpadVersion.BLANK
      : (convertBufferValue(buffer, versionField.type) as ScratchpadVersion);

    const {specs, version: foundVersion} =
      Scratchpad.getSpecificationIfExists(version);

    const {index, ...scratchpad} = specs.reduce(
      (obj, field) => {
        const index = obj.index;
        const endIndex =
          index +
          (hasLength(field)
            ? field.length
            : getMiddlewareValueByteSize(field.type));

        // return type of subarray return an array instead of Buffer
        const sliced = Buffer.from(buffer.subarray(index, endIndex));

        const value = convertBufferValue(sliced, field.type) as any;

        // String fields need to be trimmed of \x00 characters
        const normalised = field.type === 'string' ? trimChar(value) : value;

        obj[field.key as keyof ScratchpadType] = normalised;
        obj.index = endIndex;

        return obj;
      },
      {index: 1, version: foundVersion} as ScratchpadType & {index: number},
    );

    const migrated = Scratchpad.migrateIfNecesssary(scratchpad);

    return new Scratchpad(migrated);
  }

  /**
   * Get the initial values used to build the scratchpad
   * @returns a mood light, a heat profile, or both
   */
  public segregate(): ToMoodLightOrHeatProfile {
    switch (this.scratchpad.version) {
      case ScratchpadVersion.LANTERN_CUSTOM:
      case ScratchpadVersion.LANTERN_CUSTOM_0:
        return {
          moodLight: {
            id: this.scratchpad.moodUlid,
            category: MoodLightCategory.Custom,
            modified: fromSecondsTimestamp(this.scratchpad.moodDateModified),
            name: this.scratchpad.moodName,
            projector: {
              meta: {
                userColors: this.scratchpad.colors,
                moodType: this.scratchpad.moodType,
                tempoFrac: this.scratchpad.tempo,
                originalMoodLightId: (
                  this.scratchpad as ScratchpadLanternCustom
                ).originalMoodUlid,
              },
            },
          },
        };
      case ScratchpadVersion.LANTERN_EXCLUSIVE:
        return {
          moodLight: {
            id: this.scratchpad.moodUlid,
            category: MoodLightCategory.Exclusive,
          } as const,
        };
      case ScratchpadVersion.PROFILE:
        return {
          profile: {
            id: this.scratchpad.heatProfileUlid,
            modified: fromSecondsTimestamp(
              this.scratchpad.heatProfileDateModified,
            ),
          },
        };
      case ScratchpadVersion.PROFILE_CUSTOM:
      case ScratchpadVersion.PROFILE_CUSTOM_0:
        return {
          profile: {
            id: this.scratchpad.heatProfileUlid,
            modified: fromSecondsTimestamp(
              this.scratchpad.heatProfileDateModified,
            ),
          },
          moodLight: {
            id: this.scratchpad.moodUlid,
            category: MoodLightCategory.Custom,
            modified: fromSecondsTimestamp(this.scratchpad.moodDateModified),
            name: this.scratchpad.moodName,
            projector: {
              meta: {
                userColors: this.scratchpad.colors,
                moodType: this.scratchpad.moodType,
                tempoFrac: this.scratchpad.tempo,
                originalMoodLightId: (
                  this.scratchpad as ScratchpadProfileCustom
                ).originalMoodUlid,
              },
            },
          } as const,
        };
      case ScratchpadVersion.PROFILE_EXCLUSIVE:
        return {
          profile: {
            id: this.scratchpad.heatProfileUlid,
            modified: fromSecondsTimestamp(
              this.scratchpad.heatProfileDateModified,
            ),
          },
          moodLight: {
            id: this.scratchpad.moodUlid,
            category: MoodLightCategory.Exclusive,
          } as const,
        };
      default:
        return {};
    }
  }

  /**
   * Clean provided value of all unnecessary properties required of the scratchpad version
   * @param value provided value containing aggregated scratchpad properties
   * @returns value containing only required properties
   */
  private static sanitize(value: AggregatedScratchpad): ScratchpadType {
    const {specs, version} = Scratchpad.getSpecificationIfExists(value.version);

    const keys = specs.map(({key}) => key);

    return keys.reduce(
      (obj, key) => {
        obj[key] = value[key as keyof AggregatedScratchpad];

        return obj;
      },
      {version} as Record<string, any>,
    ) as ScratchpadType;
  }

  /**
   * Get scratchpad version specification
   * @param version scratchpad version
   * @returns found version or blank if none exists
   */
  private static getSpecificationIfExists(version: ScratchpadVersion) {
    const specs = encodings[version];

    if (!specs)
      return {
        specs: encodings[ScratchpadVersion.BLANK],
        version: ScratchpadVersion.BLANK,
      };

    return {specs, version};
  }

  /**
   * Gather all data necessary to build the scratchpad, regardless of its version
   * @param options a mood light, a heat profile or both
   * @returns an object containing all aggregated data
   */
  private static aggregate(
    options: FromMoodLightOrProfile,
  ): AggregatedScratchpad {
    const {moodLight, profile} = options;

    const version = Scratchpad.determineVersion(options);

    const moodUlid = moodLight?.id;
    const heatProfileUlid = profile?.id;
    const heatProfileDateModified = profile
      ? getSecondsFromMilliseconds(new Date(profile.modified).getTime())
      : undefined;
    const moodName = moodLight?.name;
    const moodDateModified = moodLight
      ? getSecondsFromMilliseconds(new Date(moodLight.modified).getTime())
      : undefined;
    const moodType = moodLight?.projector.meta.moodType;
    const tempo = moodLight?.projector.meta.tempoFrac;
    const colors = moodLight?.projector.meta.userColors;
    const originalMoodUlid =
      moodLight?.projector.meta.originalMoodLightId ?? moodUlid;

    return {
      version,
      moodUlid,
      moodName,
      moodDateModified,
      moodType,
      tempo,
      colors,
      originalMoodUlid,
      heatProfileUlid,
      heatProfileDateModified,
    };
  }

  /**
   * Determine scratchpad version from provided options
   * @param options a mood light, a heat profile or both
   * @returns determined scratchpad version
   */
  private static determineVersion(options: FromMoodLightOrProfile) {
    const {moodLight, profile} = options;

    if (moodLight) {
      if (moodLight.category === MoodLightCategory.Exclusive) {
        if (profile) return ScratchpadVersion.PROFILE_EXCLUSIVE;

        return ScratchpadVersion.LANTERN_EXCLUSIVE;
      }

      if (profile) return ScratchpadVersion.PROFILE_CUSTOM;

      return ScratchpadVersion.LANTERN_CUSTOM;
    }

    if (profile) return ScratchpadVersion.PROFILE;

    return ScratchpadVersion.BLANK;
  }

  /**
   * Migrate an older versions of a scratchpad to latest version if necessary
   * @param obj scratchpad
   * @returns migrate scratchpad
   */
  private static migrateIfNecesssary(obj: ScratchpadType): ScratchpadType {
    switch (obj.version) {
      case ScratchpadVersion.LANTERN_CUSTOM_0:
        return {
          ...obj,
          originalMoodUlid: obj.moodUlid,
          version: ScratchpadVersion.LANTERN_CUSTOM,
        };
      case ScratchpadVersion.PROFILE_CUSTOM_0:
        return {
          ...obj,
          originalMoodUlid: obj.moodUlid,
          version: ScratchpadVersion.PROFILE_CUSTOM,
        };
      default:
        return obj;
    }
  }
}
